// src/modules/gyms/hooks/use-gym-data.ts

import { useState, useEffect, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import { gymPartnerService } from "../services/gym.service";
import { GymBranch, GymService, Amenity } from "../models/gym.model";
import { AuthArgs } from "../types/auth.types";

export function useGymData(authArgs: AuthArgs) {
  const location = useLocation();
  const isGymRoute = location.pathname.startsWith("/b2b/gyms");

  const [branches, setBranches] = useState<GymBranch[]>([]);
  const [services, setServices] = useState<GymService[]>([]);
  const [amenities, setAmenities] = useState<Amenity[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadingRef = useRef(false);
  const initialLoadDone = useRef(false);
  const servicesLoadedForBrand = useRef<Set<string>>(new Set());
  const servicesLoadingBrands = useRef<Set<string>>(new Set());
  const authArgsRef = useRef(authArgs);
  authArgsRef.current = authArgs;

  // Branches (+ amenities) only — never fan-out service GETs for every branch.
  const loadData = useCallback(async () => {
    if (!authArgsRef.current.auth || !isGymRoute) {
      if (!authArgsRef.current.auth) setLoaded(true);
      return;
    }
    if (loadingRef.current || initialLoadDone.current) return;
    loadingRef.current = true;
    setLoading(true);
    setError(null);

    const args = authArgsRef.current;
    try {
      const [branchesData, amenitiesData] = await Promise.all([
        gymPartnerService.getAllBranches(args),
        Promise.resolve(gymPartnerService.getAmenities()),
      ]);

      setBranches(branchesData);
      setAmenities(amenitiesData);
    } catch (err: any) {
      setError(err.message || "Không thể tải dữ liệu đối tác gym");
    } finally {
      // ponytail: mark done even on error so a 404 doesn't retry-spam the BE
      setLoading(false);
      setLoaded(true);
      loadingRef.current = false;
      initialLoadDone.current = true;
    }
  }, [isGymRoute]);

  const loadServicesForBrand = useCallback(
    async (
      brandId: string,
      knownBranches?: Pick<GymBranch, "id" | "brandId">[],
    ) => {
      if (!brandId || !authArgsRef.current.auth) return;
      if (
        servicesLoadedForBrand.current.has(brandId) ||
        servicesLoadingBrands.current.has(brandId)
      ) {
        return;
      }
      servicesLoadingBrands.current.add(brandId);
      try {
        const data = await gymPartnerService.getServicesByBrand(
          brandId,
          authArgsRef.current,
          knownBranches,
        );
        setServices((prev) => [
          ...prev.filter((s) => s.brandId !== brandId),
          ...data,
        ]);
        servicesLoadedForBrand.current.add(brandId);
      } catch (err: any) {
        // ponytail: still mark loaded so a failing brand doesn't get re-fetched forever
        servicesLoadedForBrand.current.add(brandId);
        console.error("loadServicesForBrand error:", err);
      } finally {
        servicesLoadingBrands.current.delete(brandId);
      }
    },
    [],
  );
  useEffect(() => {
    if (!isGymRoute) {
      setBranches([]);
      setServices([]);
      setAmenities([]);
      setLoaded(true);
      setError(null);
      initialLoadDone.current = false;
      loadingRef.current = false;
      servicesLoadedForBrand.current.clear();
      servicesLoadingBrands.current.clear();
      return;
    }
    if (!authArgs.auth) {
      setLoaded(true);
      return;
    }
    loadData();
  }, [authArgs.auth, isGymRoute, loadData]);

  const refresh = useCallback(async () => {
    initialLoadDone.current = false;
    const brandsToReload = Array.from(servicesLoadedForBrand.current);
    servicesLoadedForBrand.current.clear();
    await loadData();
    await Promise.all(brandsToReload.map((id) => loadServicesForBrand(id)));
  }, [loadData, loadServicesForBrand]);

  return {
    branches,
    setBranches,
    services,
    setServices,
    amenities,
    loading,
    loaded,
    error,
    refresh,
    loadServicesForBrand,
  };
}
